import React, { useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import {
  Code,
  Copy,
  Check,
  ExternalLink,
  BookOpen,
  Zap,
  Shield,
  Globe,
  Database,
  Camera,
  Heart,
  Plus,
  Eye,
  Terminal,
  Search,
  BarChart3,
  FileText,
  Trash2,
  Key,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Edit,
  List,
  Activity,
  Info,
  User,
  Settings,
} from "lucide-react";

const ApiDocumentationDetailed = () => {
  const { themeClasses } = useTheme();
  const [copiedCode, setCopiedCode] = useState("");
  const [activeSection, setActiveSection] = useState("overview");
  const [selectedLanguage, setSelectedLanguage] = useState("javascript");
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(""), 2000);
  };

  const sections = [
    { id: "overview", name: "API Overview", icon: BookOpen },
    { id: "authentication", name: "Authentication", icon: Shield },
    { id: "pet-management", name: "Pet Management CRUD", icon: Heart },
    { id: "health-records", name: "Health Records CRUD", icon: FileText },
    {
      id: "vaccination-records",
      name: "Vaccination Records CRUD",
      icon: Shield,
    },
    { id: "user-management", name: "User Management CRUD", icon: User },
    { id: "api-key-management", name: "API Key Management CRUD", icon: Key },
    { id: "breed-database", name: "Breed Database", icon: Database },
    { id: "utility-functions", name: "Utility Functions", icon: Settings },
    { id: "rate-limits", name: "Rate Limits", icon: Clock },
    { id: "errors", name: "Error Handling", icon: AlertTriangle },
    { id: "sdks", name: "SDKs & Examples", icon: Code },
  ];

  // Pet Management CRUD
  const petManagementEndpoints = [
    {
      id: "generate-pet-biometric",
      name: "Generate Pet Biometric",
      method: "POST",
      path: "/api/generatePetBiometric",
      icon: Camera,
      description:
        "Generate advanced biometric fingerprint from multiple pet images for unique identification",
      permission: "register",
      rateLimit: "20/min",
      crudOperation: "CREATE",
      parameters: {
        images: {
          type: "array",
          required: true,
          description: "Array of image objects (minimum 3 required)",
          items: {
            type: "object",
            properties: {
              type: {
                type: "string",
                enum: [
                  "frontFace",
                  "leftProfile",
                  "rightProfile",
                  "fullBody",
                  "distinctiveMarks",
                ],
                description:
                  "Type of image (frontFace, leftProfile, rightProfile required)",
              },
              base64Data: {
                type: "string",
                description: "Base64 encoded image data",
              },
            },
          },
        },
        petData: {
          type: "object",
          required: false,
          description:
            "Optional pet metadata for enhanced biometric processing",
          properties: {
            species: { type: "string", description: "Pet species" },
            breed: { type: "string", description: "Pet breed" },
            age: { type: "number", description: "Pet age" },
          },
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether biometric generation was successful",
        },
        biometricID: {
          type: "object",
          properties: {
            fingerprint: {
              type: "string",
              description: "Unique pet fingerprint hash",
            },
            confidence: {
              type: "number",
              description: "Overall confidence score (0-1)",
            },
            features: {
              type: "object",
              properties: {
                faceEmbeddings: {
                  type: "number",
                  description: "Number of face embeddings extracted",
                },
                bodyFeatures: {
                  type: "number",
                  description: "Number of body features extracted",
                },
                totalImages: {
                  type: "number",
                  description: "Total images processed",
                },
              },
            },
            composite: {
              type: "object",
              description: "Composite biometric data",
            },
            generatedAt: {
              type: "string",
              description: "Generation timestamp",
            },
            version: { type: "string", description: "Biometric version" },
          },
        },
        processingStats: {
          type: "object",
          properties: {
            imagesProcessed: {
              type: "number",
              description: "Number of images successfully processed",
            },
            imagesProvided: {
              type: "number",
              description: "Number of images provided",
            },
            faceEmbeddingsExtracted: {
              type: "number",
              description: "Face embeddings extracted",
            },
            averageConfidence: {
              type: "number",
              description: "Average processing confidence",
            },
          },
        },
      },
    },
    {
      id: "generate-pet-biometric-v2",
      name: "Generate Pet Biometric V2 (URL-based)",
      method: "POST",
      path: "/api/generatePetBiometricV2",
      icon: Camera,
      description:
        "Generate advanced biometric fingerprint from image URLs (more scalable approach)",
      permission: "register",
      rateLimit: "20/min",
      crudOperation: "CREATE",
      parameters: {
        imageUrls: {
          type: "array",
          required: true,
          description: "Array of image URL objects (minimum 3 required)",
          items: {
            type: "object",
            properties: {
              type: {
                type: "string",
                enum: [
                  "frontFace",
                  "leftProfile",
                  "rightProfile",
                  "fullBody",
                  "distinctiveMarks",
                ],
                description:
                  "Type of image (frontFace, leftProfile, rightProfile required)",
              },
              url: {
                type: "string",
                description: "Firebase Storage URL or accessible image URL",
              },
            },
          },
        },
        petData: {
          type: "object",
          required: false,
          description:
            "Optional pet metadata for enhanced biometric processing",
          properties: {
            species: { type: "string", description: "Pet species" },
            breed: { type: "string", description: "Pet breed" },
            age: { type: "number", description: "Pet age" },
          },
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether biometric generation was successful",
        },
        biometricID: {
          type: "object",
          properties: {
            fingerprint: {
              type: "string",
              description: "Unique pet fingerprint hash",
            },
            confidence: {
              type: "number",
              description: "Overall confidence score (0-1)",
            },
            features: {
              type: "object",
              properties: {
                faceEmbeddings: {
                  type: "number",
                  description: "Number of face embeddings extracted",
                },
                bodyFeatures: {
                  type: "number",
                  description: "Number of body features extracted",
                },
                totalImages: {
                  type: "number",
                  description: "Total images provided",
                },
                processedImages: {
                  type: "number",
                  description: "Images successfully processed",
                },
              },
            },
            composite: {
              type: "object",
              description: "Composite biometric data",
            },
            generatedAt: {
              type: "string",
              description: "Generation timestamp",
            },
            version: { type: "string", description: "Biometric version (2.0)" },
          },
        },
        processingStats: {
          type: "object",
          properties: {
            imagesProcessed: {
              type: "number",
              description: "Number of images successfully processed",
            },
            imagesProvided: {
              type: "number",
              description: "Number of images provided",
            },
            faceEmbeddingsExtracted: {
              type: "number",
              description: "Face embeddings extracted",
            },
            averageConfidence: {
              type: "number",
              description: "Average processing confidence",
            },
            processedUrls: {
              type: "array",
              description: "Successfully processed image URLs",
            },
          },
        },
      },
    },
    {
      id: "register-pet",
      name: "Register Pet",
      method: "POST",
      path: "/api/registerPet",
      icon: Plus,
      description:
        "Register a new pet profile with photos and owner information",
      permission: "register",
      rateLimit: "50/min",
      crudOperation: "CREATE",
      parameters: {
        name: { type: "string", required: true, description: "Pet's name" },
        species: {
          type: "string",
          required: true,
          description: "Pet species (Dog, Cat, etc.)",
        },
        breed: { type: "string", required: true, description: "Pet breed" },
        age: {
          type: "number",
          required: false,
          description: "Pet age in years",
        },
        imageBase64: {
          type: "string",
          required: true,
          description: "Base64 encoded pet photo",
        },
        contactInfo: {
          type: "object",
          required: true,
          properties: {
            name: { type: "string", description: "Owner's name" },
            phone: { type: "string", description: "Owner's phone number" },
            email: { type: "string", description: "Owner's email address" },
          },
        },
        description: {
          type: "string",
          required: false,
          description: "Pet description",
        },
        biometricID: {
          type: "object",
          required: false,
          description:
            "Pre-generated biometric fingerprint from generatePetBiometric",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether registration was successful",
        },
        petId: { type: "string", description: "Unique pet identifier" },
        qrCode: { type: "string", description: "QR code data for the pet" },
        message: { type: "string", description: "Success message" },
      },
    },
    {
      id: "get-pet-profile",
      name: "Get Pet Profile",
      method: "GET",
      path: "/api/getPetProfile",
      icon: Eye,
      description: "Retrieve detailed information about a registered pet",
      permission: "read",
      rateLimit: "200/min",
      crudOperation: "READ",
      parameters: {
        petId: {
          type: "string",
          required: true,
          description: "Unique pet identifier",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "object",
          properties: {
            id: { type: "string", description: "Pet ID" },
            name: { type: "string", description: "Pet name" },
            species: { type: "string", description: "Pet species" },
            breed: { type: "string", description: "Pet breed" },
            age: { type: "number", description: "Pet age" },
            photos: { type: "array", description: "Array of pet photos" },
            contactInfo: {
              type: "object",
              description: "Owner contact information",
            },
            createdAt: {
              type: "string",
              description: "Registration timestamp",
            },
          },
        },
      },
    },
    {
      id: "identify-pet",
      name: "Identify Pet",
      method: "POST",
      path: "/api/identifyPet",
      icon: Camera,
      description: "Identify a pet from an uploaded image using AI",
      permission: "identify",
      rateLimit: "100/min",
      crudOperation: "READ",
      parameters: {
        imageBase64: {
          type: "string",
          required: true,
          description: "Base64 encoded image data",
        },
        location: {
          type: "string",
          required: false,
          description: "Optional location information",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether identification was successful",
        },
        data: {
          type: "object",
          properties: {
            pet_id: {
              type: "string",
              description: "Unique pet identifier if found",
            },
            species: { type: "string", description: "Identified species" },
            breed: { type: "string", description: "Identified breed" },
            confidence: {
              type: "number",
              description: "Confidence score (0-1)",
            },
            owner_contact: {
              type: "string",
              description: "Owner contact info if pet is registered",
            },
          },
        },
      },
    },
    {
      id: "identify-pet-from-url",
      name: "Identify Pet from URL",
      method: "POST",
      path: "/api/identifyPetFromUrl",
      icon: Camera,
      description:
        "Identify a pet from an image URL using AI (recommended for large images)",
      permission: "identify",
      rateLimit: "100/min",
      crudOperation: "READ",
      parameters: {
        imageUrl: {
          type: "string",
          required: true,
          description: "URL of the image to identify",
        },
        location: {
          type: "string",
          required: false,
          description: "Optional location information",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether identification was successful",
        },
        data: {
          type: "object",
          properties: {
            pet_id: {
              type: "string",
              description: "Unique pet identifier if found",
            },
            species: { type: "string", description: "Identified species" },
            breed: { type: "string", description: "Identified breed" },
            confidence: {
              type: "number",
              description: "Confidence score (0-1)",
            },
            owner_contact: {
              type: "string",
              description: "Owner contact info if pet is registered",
            },
            sourceUrl: {
              type: "string",
              description: "Original image URL used for identification",
            },
          },
        },
      },
    },
    {
      id: "update-pet-profile",
      name: "Update Pet Profile",
      method: "PUT",
      path: "/api/updatePetProfile",
      icon: Edit,
      description: "Update an existing pet profile",
      permission: "update",
      rateLimit: "100/min",
      crudOperation: "UPDATE",
      parameters: {
        petId: {
          type: "string",
          required: true,
          description: "Pet ID to update",
        },
        updates: {
          type: "object",
          required: true,
          properties: {
            name: { type: "string", description: "Updated pet name" },
            age: { type: "number", description: "Updated pet age" },
            description: { type: "string", description: "Updated description" },
            contactInfo: {
              type: "object",
              description: "Updated contact information",
            },
          },
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether update was successful",
        },
        message: { type: "string", description: "Update confirmation message" },
      },
    },
    {
      id: "get-user-pets",
      name: "Get User Pets",
      method: "GET",
      path: "/api/getUserPets",
      icon: List,
      description: "Get all pets registered by the current user",
      permission: "read",
      rateLimit: "200/min",
      crudOperation: "READ",
      parameters: {},
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        pets: {
          type: "array",
          description: "Array of user's pets",
          items: {
            type: "object",
            properties: {
              id: { type: "string", description: "Pet ID" },
              name: { type: "string", description: "Pet name" },
              species: { type: "string", description: "Pet species" },
              breed: { type: "string", description: "Pet breed" },
              age: { type: "number", description: "Pet age" },
              imageUrl: { type: "string", description: "Pet photo URL" },
              registrationDate: {
                type: "string",
                description: "Registration date",
              },
              contactInfo: {
                type: "object",
                description: "Owner contact info",
              },
            },
          },
        },
      },
    },
    {
      id: "delete-pet",
      name: "Delete Pet",
      method: "DELETE",
      path: "/api/deletePet",
      icon: Trash2,
      description:
        "Delete a pet profile permanently with automatic storage cleanup",
      permission: "delete",
      rateLimit: "50/min",
      crudOperation: "DELETE",
      parameters: {
        petId: {
          type: "string",
          required: true,
          description: "Pet ID to delete",
        },
        confirmEmail: {
          type: "string",
          required: true,
          description: "Owner email for confirmation",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether deletion was successful",
        },
        message: {
          type: "string",
          description: "Deletion confirmation message",
        },
        storageFiles: {
          type: "number",
          description: "Number of storage files processed for cleanup",
        },
        deletedFiles: {
          type: "array",
          description: "Array of successfully deleted storage file paths",
          items: {
            type: "string",
            description: "Storage file path that was deleted",
          },
        },
      },
    },
  ];

  // Health Records CRUD
  const healthRecordsEndpoints = [
    {
      id: "extract-health-record",
      name: "Extract Health Record",
      method: "POST",
      path: "/api/extractHealthRecord",
      icon: FileText,
      description: "Extract health information from uploaded documents",
      permission: "health",
      rateLimit: "50/min",
      crudOperation: "CREATE",
      parameters: {
        imageBase64: {
          type: "string",
          required: true,
          description: "Base64 encoded document image",
        },
        fileName: {
          type: "string",
          required: true,
          description: "Original file name",
        },
        petId: {
          type: "string",
          required: true,
          description: "Associated pet ID",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether extraction was successful",
        },
        data: {
          type: "object",
          properties: {
            recordId: {
              type: "string",
              description: "Generated health record ID",
            },
            extractedData: {
              type: "object",
              description: "Extracted health information",
            },
            confidence: {
              type: "number",
              description: "Extraction confidence score",
            },
          },
        },
      },
    },
    {
      id: "get-health-records",
      name: "Get Health Records",
      method: "GET",
      path: "/api/getHealthRecords",
      icon: Eye,
      description: "Get detailed health records for a specific record ID",
      permission: "read",
      rateLimit: "200/min",
      crudOperation: "READ",
      parameters: {
        recordId: {
          type: "string",
          required: true,
          description: "Health record ID",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "object",
          properties: {
            recordId: { type: "string", description: "Health record ID" },
            petId: { type: "string", description: "Associated pet ID" },
            extractedData: {
              type: "object",
              description: "Health information",
            },
            createdAt: {
              type: "string",
              description: "Record creation timestamp",
            },
          },
        },
      },
    },
    {
      id: "list-health-records",
      name: "List Health Records",
      method: "GET",
      path: "/api/listHealthRecords",
      icon: List,
      description: "List all health records for a pet",
      permission: "read",
      rateLimit: "200/min",
      crudOperation: "READ",
      parameters: {
        petId: {
          type: "string",
          required: true,
          description: "Pet ID to get records for",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "array",
          description: "Array of health records",
          items: {
            type: "object",
            properties: {
              recordId: { type: "string", description: "Health record ID" },
              extractedData: {
                type: "object",
                description: "Health information",
              },
              createdAt: {
                type: "string",
                description: "Record creation timestamp",
              },
            },
          },
        },
      },
    },
    {
      id: "update-health-record",
      name: "Update Health Record",
      method: "PUT",
      path: "/api/updateHealthRecord",
      icon: Edit,
      description: "Update an existing health record",
      permission: "health",
      rateLimit: "100/min",
      crudOperation: "UPDATE",
      parameters: {
        recordId: {
          type: "string",
          required: true,
          description: "Health record ID to update",
        },
        updates: {
          type: "object",
          required: true,
          properties: {
            medications: {
              type: "array",
              description: "Updated medications list",
            },
            procedures: {
              type: "array",
              description: "Updated procedures list",
            },
            notes: { type: "string", description: "Updated notes" },
          },
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether update was successful",
        },
        message: { type: "string", description: "Update confirmation message" },
      },
    },
    {
      id: "delete-health-record",
      name: "Delete Health Record",
      method: "DELETE",
      path: "/api/deleteHealthRecord",
      icon: Trash2,
      description: "Delete a health record permanently",
      permission: "delete",
      rateLimit: "50/min",
      crudOperation: "DELETE",
      parameters: {
        recordId: {
          type: "string",
          required: true,
          description: "Health record ID to delete",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether deletion was successful",
        },
        message: {
          type: "string",
          description: "Deletion confirmation message",
        },
      },
    },
  ];

  // Vaccination Records CRUD
  const vaccinationRecordsEndpoints = [
    {
      id: "store-pet-vaccination-data",
      name: "Store Vaccination Data",
      method: "POST",
      path: "/api/storePetVaccinationData",
      icon: Shield,
      description: "Store vaccination data for a pet",
      permission: "health",
      rateLimit: "50/min",
      crudOperation: "CREATE",
      parameters: {
        petId: { type: "string", required: true, description: "Pet ID" },
        vaccinationData: {
          type: "object",
          required: true,
          properties: {
            vaccineName: { type: "string", description: "Name of the vaccine" },
            dateAdministered: {
              type: "string",
              description: "Date vaccine was given",
            },
            veterinarian: {
              type: "string",
              description: "Administering veterinarian",
            },
            clinic: { type: "string", description: "Clinic name" },
            nextDueDate: {
              type: "string",
              description: "Next vaccination due date",
            },
          },
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether storage was successful",
        },
        vaccinationId: {
          type: "string",
          description: "Generated vaccination record ID",
        },
        message: { type: "string", description: "Success message" },
      },
    },
    {
      id: "get-vaccination-records",
      name: "Get Vaccination Records",
      method: "GET",
      path: "/api/getVaccinationRecords",
      icon: Eye,
      description: "Get detailed vaccination records for a specific record ID",
      permission: "read",
      rateLimit: "200/min",
      crudOperation: "READ",
      parameters: {
        vaccinationId: {
          type: "string",
          required: true,
          description: "Vaccination record ID",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "object",
          properties: {
            vaccinationId: {
              type: "string",
              description: "Vaccination record ID",
            },
            petId: { type: "string", description: "Associated pet ID" },
            vaccinationData: {
              type: "object",
              description: "Vaccination information",
            },
            createdAt: {
              type: "string",
              description: "Record creation timestamp",
            },
          },
        },
      },
    },
    {
      id: "list-vaccination-records",
      name: "List Vaccination Records",
      method: "GET",
      path: "/api/listVaccinationRecords",
      icon: List,
      description: "List all vaccination records for a pet",
      permission: "read",
      rateLimit: "200/min",
      crudOperation: "READ",
      parameters: {
        petId: {
          type: "string",
          required: true,
          description: "Pet ID to get vaccination records for",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "array",
          description: "Array of vaccination records",
          items: {
            type: "object",
            properties: {
              vaccinationId: {
                type: "string",
                description: "Vaccination record ID",
              },
              vaccinationData: {
                type: "object",
                description: "Vaccination information",
              },
              createdAt: {
                type: "string",
                description: "Record creation timestamp",
              },
            },
          },
        },
      },
    },
    {
      id: "delete-vaccination",
      name: "Delete Vaccination Record",
      method: "DELETE",
      path: "/api/deleteVaccination",
      icon: Trash2,
      description: "Delete a vaccination record permanently",
      permission: "delete",
      rateLimit: "50/min",
      crudOperation: "DELETE",
      parameters: {
        vaccinationId: {
          type: "string",
          required: true,
          description: "Vaccination record ID to delete",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether deletion was successful",
        },
        message: {
          type: "string",
          description: "Deletion confirmation message",
        },
      },
    },
  ];

  // User Management CRUD
  const userManagementEndpoints = [
    {
      id: "get-user-profile",
      name: "Get User Profile",
      method: "GET",
      path: "/api/getUserProfile",
      icon: User,
      description: "Get comprehensive user profile data",
      permission: "read",
      rateLimit: "200/min",
      crudOperation: "READ",
      parameters: {},
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "object",
          properties: {
            uid: { type: "string", description: "User ID" },
            email: { type: "string", description: "User email" },
            displayName: { type: "string", description: "Display name" },
            preferences: { type: "object", description: "User preferences" },
            stats: { type: "object", description: "User statistics" },
          },
        },
      },
    },
    {
      id: "update-user-profile",
      name: "Update User Profile",
      method: "PUT",
      path: "/api/updateUserProfile",
      icon: Edit,
      description: "Update user profile information",
      permission: "update",
      rateLimit: "100/min",
      crudOperation: "UPDATE",
      parameters: {
        updates: {
          type: "object",
          required: true,
          properties: {
            firstName: { type: "string", description: "Updated first name" },
            lastName: { type: "string", description: "Updated last name" },
            preferences: { type: "object", description: "Updated preferences" },
          },
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether update was successful",
        },
        message: { type: "string", description: "Update confirmation message" },
      },
    },
    {
      id: "get-user-stats",
      name: "Get User Stats",
      method: "GET",
      path: "/api/getUserStats",
      icon: BarChart3,
      description: "Get user statistics and usage data",
      permission: "read",
      rateLimit: "200/min",
      crudOperation: "READ",
      parameters: {},
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "object",
          properties: {
            petsRegistered: {
              type: "number",
              description: "Number of pets registered",
            },
            identificationsPerformed: {
              type: "number",
              description: "Number of identifications",
            },
            profileCompleteness: {
              type: "number",
              description: "Profile completeness percentage",
            },
          },
        },
      },
    },
    {
      id: "delete-user-account",
      name: "Delete User Account",
      method: "DELETE",
      path: "/api/deleteUserAccount",
      icon: Trash2,
      description: "Permanently delete user account and all data",
      permission: "delete",
      rateLimit: "10/min",
      crudOperation: "DELETE",
      parameters: {
        confirmEmail: {
          type: "string",
          required: true,
          description: "User email for confirmation",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether deletion was successful",
        },
        message: {
          type: "string",
          description: "Deletion confirmation message",
        },
      },
    },
  ];

  // API Key Management CRUD
  const apiKeyManagementEndpoints = [
    {
      id: "create-api-key",
      name: "Create API Key",
      method: "POST",
      path: "/api/createApiKey",
      icon: Key,
      description: "Create a new API key for enterprise users",
      permission: "admin",
      rateLimit: "10/min",
      crudOperation: "CREATE",
      parameters: {
        name: { type: "string", required: true, description: "API key name" },
        permissions: {
          type: "array",
          required: true,
          description: "Array of permissions",
          items: {
            type: "string",
            enum: ["identify", "register", "health", "breed"],
          },
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether creation was successful",
        },
        apiKey: { type: "string", description: "Generated API key" },
        keyId: { type: "string", description: "API key ID" },
        message: { type: "string", description: "Success message" },
      },
    },
    {
      id: "list-api-keys",
      name: "List API Keys",
      method: "GET",
      path: "/api/listApiKeys",
      icon: List,
      description: "List all API keys for the authenticated user",
      permission: "read",
      rateLimit: "100/min",
      crudOperation: "READ",
      parameters: {},
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "array",
          description: "Array of API keys",
          items: {
            type: "object",
            properties: {
              keyId: { type: "string", description: "API key ID" },
              name: { type: "string", description: "API key name" },
              permissions: { type: "array", description: "Key permissions" },
              createdAt: { type: "string", description: "Creation timestamp" },
              lastUsed: { type: "string", description: "Last usage timestamp" },
            },
          },
        },
      },
    },
    {
      id: "get-api-key-stats",
      name: "Get API Key Stats",
      method: "GET",
      path: "/api/getApiKeyStats",
      icon: BarChart3,
      description: "Get usage statistics for API keys",
      permission: "read",
      rateLimit: "100/min",
      crudOperation: "READ",
      parameters: {
        keyId: {
          type: "string",
          required: false,
          description: "Specific API key ID (optional)",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "object",
          properties: {
            totalRequests: {
              type: "number",
              description: "Total API requests",
            },
            requestsToday: { type: "number", description: "Requests today" },
            requestsThisMonth: {
              type: "number",
              description: "Requests this month",
            },
            rateLimitHits: {
              type: "number",
              description: "Rate limit violations",
            },
          },
        },
      },
    },
    {
      id: "update-api-key",
      name: "Update API Key",
      method: "PUT",
      path: "/api/updateApiKey",
      icon: Edit,
      description: "Update API key permissions or name",
      permission: "admin",
      rateLimit: "50/min",
      crudOperation: "UPDATE",
      parameters: {
        keyId: {
          type: "string",
          required: true,
          description: "API key ID to update",
        },
        updates: {
          type: "object",
          required: true,
          properties: {
            name: { type: "string", description: "Updated API key name" },
            permissions: {
              type: "array",
              description: "Updated permissions array",
            },
          },
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether update was successful",
        },
        message: { type: "string", description: "Update confirmation message" },
      },
    },
    {
      id: "revoke-api-key",
      name: "Revoke API Key",
      method: "DELETE",
      path: "/api/revokeApiKey",
      icon: Trash2,
      description: "Revoke/delete an API key",
      permission: "admin",
      rateLimit: "50/min",
      crudOperation: "DELETE",
      parameters: {
        keyId: {
          type: "string",
          required: true,
          description: "API key ID to revoke",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether revocation was successful",
        },
        message: {
          type: "string",
          description: "Revocation confirmation message",
        },
      },
    },
  ];

  // Breed Database
  const breedEndpoints = [
    {
      id: "detect-dog-breed",
      name: "Detect Dog Breed",
      method: "POST",
      path: "/api/detectDogBreed",
      icon: Search,
      description: "Detect dog breed from an image using specialized AI",
      permission: "breed",
      rateLimit: "100/min",
      crudOperation: "UTILITY",
      parameters: {
        imageBase64: {
          type: "string",
          required: true,
          description: "Base64 encoded dog image",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether detection was successful",
        },
        data: {
          type: "object",
          properties: {
            breed: { type: "string", description: "Detected breed name" },
            confidence: {
              type: "number",
              description: "Confidence score (0-1)",
            },
            alternatives: {
              type: "array",
              description: "Alternative breed suggestions",
            },
          },
        },
      },
    },
    {
      id: "identify-dog-breed",
      name: "Identify Dog Breed",
      method: "POST",
      path: "/api/identifyDogBreed",
      icon: Camera,
      description: "Alternative dog breed identification endpoint",
      permission: "breed",
      rateLimit: "100/min",
      crudOperation: "UTILITY",
      parameters: {
        imageBase64: {
          type: "string",
          required: true,
          description: "Base64 encoded dog image",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether identification was successful",
        },
        data: {
          type: "object",
          properties: {
            breed: { type: "string", description: "Identified breed name" },
            confidence: {
              type: "number",
              description: "Confidence score (0-1)",
            },
          },
        },
      },
    },
    {
      id: "get-breed-info",
      name: "Get Breed Information",
      method: "GET",
      path: "/api/getBreedInfo",
      icon: Database,
      description: "Get detailed information about a specific breed",
      permission: "read",
      rateLimit: "200/min",
      crudOperation: "UTILITY",
      parameters: {
        breedName: {
          type: "string",
          required: true,
          description: "Name of the breed",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "object",
          properties: {
            name: { type: "string", description: "Breed name" },
            size: { type: "string", description: "Size category" },
            temperament: { type: "array", description: "Temperament traits" },
            exercise_needs: {
              type: "string",
              description: "Exercise requirements",
            },
            grooming_needs: {
              type: "string",
              description: "Grooming requirements",
            },
            health_issues: {
              type: "array",
              description: "Common health issues",
            },
          },
        },
      },
    },
    {
      id: "compare-breeds",
      name: "Compare Breeds",
      method: "POST",
      path: "/api/compareBreeds",
      icon: BarChart3,
      description: "Compare characteristics between multiple breeds",
      permission: "read",
      rateLimit: "100/min",
      crudOperation: "UTILITY",
      parameters: {
        breeds: {
          type: "array",
          required: true,
          description: "Array of breed names to compare",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether comparison was successful",
        },
        data: {
          type: "object",
          properties: {
            comparison: {
              type: "object",
              description: "Breed comparison data",
            },
            similarities: {
              type: "array",
              description: "Common characteristics",
            },
            differences: { type: "array", description: "Key differences" },
          },
        },
      },
    },
    {
      id: "get-breed-recommendations",
      name: "Get Breed Recommendations",
      method: "POST",
      path: "/api/getBreedRecommendations",
      icon: Heart,
      description: "Get breed recommendations based on user preferences",
      permission: "read",
      rateLimit: "100/min",
      crudOperation: "UTILITY",
      parameters: {
        preferences: {
          type: "object",
          required: true,
          properties: {
            size: {
              type: "string",
              description: "Preferred size (small, medium, large)",
            },
            activity_level: {
              type: "string",
              description: "Activity level preference",
            },
            living_situation: {
              type: "string",
              description: "Living situation",
            },
            experience_level: {
              type: "string",
              description: "Owner experience level",
            },
          },
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "array",
          description: "Array of recommended breeds",
          items: {
            type: "object",
            properties: {
              breed: { type: "string", description: "Breed name" },
              match_score: {
                type: "number",
                description: "Match score (0-100)",
              },
              reasons: {
                type: "array",
                description: "Reasons for recommendation",
              },
            },
          },
        },
      },
    },
    {
      id: "list-available-breeds",
      name: "List Available Breeds",
      method: "GET",
      path: "/api/listAvailableBreeds",
      icon: List,
      description: "Get list of all available breeds in the database",
      permission: "read",
      rateLimit: "200/min",
      crudOperation: "UTILITY",
      parameters: {
        species: {
          type: "string",
          required: false,
          description: "Filter by species (dog, cat, etc.)",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "array",
          description: "Array of available breeds",
          items: {
            type: "object",
            properties: {
              name: { type: "string", description: "Breed name" },
              species: { type: "string", description: "Species" },
              popularity: { type: "number", description: "Popularity ranking" },
            },
          },
        },
      },
    },
    {
      id: "search-breeds",
      name: "Search Breeds",
      method: "GET",
      path: "/api/searchBreeds",
      icon: Search,
      description: "Search for breeds by name or characteristics",
      permission: "read",
      rateLimit: "200/min",
      crudOperation: "UTILITY",
      parameters: {
        query: { type: "string", required: true, description: "Search query" },
        limit: {
          type: "number",
          required: false,
          description: "Maximum results to return",
        },
      },
      response: {
        success: {
          type: "boolean",
          description: "Whether search was successful",
        },
        data: {
          type: "array",
          description: "Array of matching breeds",
          items: {
            type: "object",
            properties: {
              name: { type: "string", description: "Breed name" },
              species: { type: "string", description: "Species" },
              match_score: {
                type: "number",
                description: "Search relevance score",
              },
            },
          },
        },
      },
    },
  ];

  // Utility Functions
  const utilityEndpoints = [
    {
      id: "get-stats",
      name: "Get System Stats",
      method: "GET",
      path: "/api/getStats",
      icon: BarChart3,
      description: "Get system-wide statistics and analytics",
      permission: "admin",
      rateLimit: "100/min",
      crudOperation: "UTILITY",
      parameters: {},
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "object",
          properties: {
            totalPets: { type: "number", description: "Total pets registered" },
            totalUsers: { type: "number", description: "Total users" },
            totalIdentifications: {
              type: "number",
              description: "Total identifications performed",
            },
            systemHealth: {
              type: "string",
              description: "System health status",
            },
          },
        },
      },
    },
    {
      id: "health-check",
      name: "Health Check",
      method: "GET",
      path: "/api/healthCheck",
      icon: Activity,
      description: "Check API health and status",
      permission: "public",
      rateLimit: "1000/min",
      crudOperation: "UTILITY",
      parameters: {},
      response: {
        success: { type: "boolean", description: "Whether API is healthy" },
        status: { type: "string", description: "Health status" },
        timestamp: { type: "string", description: "Check timestamp" },
        version: { type: "string", description: "API version" },
      },
    },
    {
      id: "api",
      name: "API Info",
      method: "GET",
      path: "/api/api",
      icon: Info,
      description: "Get API information and metadata",
      permission: "public",
      rateLimit: "1000/min",
      crudOperation: "UTILITY",
      parameters: {},
      response: {
        success: {
          type: "boolean",
          description: "Whether request was successful",
        },
        data: {
          type: "object",
          properties: {
            name: { type: "string", description: "API name" },
            version: { type: "string", description: "API version" },
            description: { type: "string", description: "API description" },
            endpoints: {
              type: "number",
              description: "Total number of endpoints",
            },
          },
        },
      },
    },
  ];

  const codeExamples = {
    javascript: {
      "identify-pet": `// Pet Identification
const identifyPet = async (imageBase64, apiKey) => {
  const response = await fetch('https://us-central1-petid-77a78.cloudfunctions.net/identifyPet', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': \`Bearer \${apiKey}\`
    },
    body: JSON.stringify({
      imageBase64: imageBase64,
      location: 'Optional location'
    })
  });
  
  return await response.json();
};

// Usage
const result = await identifyPet('data:image/jpeg;base64,...', 'your-api-key');
if (result.success) {
  console.log('Pet identified:', result.data);
}`,

      "identify-pet-from-url": `// Pet Identification from URL (Recommended)
const identifyPetFromUrl = async (imageUrl, apiKey) => {
  const response = await fetch('https://us-central1-petid-77a78.cloudfunctions.net/identifyPetFromUrl', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': \`Bearer \${apiKey}\`
    },
    body: JSON.stringify({
      imageUrl: imageUrl,
      location: 'Optional location'
    })
  });
  
  return await response.json();
};

// Usage - Better for large images and external URLs
const result = await identifyPetFromUrl('https://example.com/pet-image.jpg', 'your-api-key');
if (result.success) {
  console.log('Pet identified:', result.data);
  console.log('Source URL:', result.data.sourceUrl);
}`,

      "generate-pet-biometric": `// Generate Pet Biometric Fingerprint
const generatePetBiometric = async (images, petData, apiKey) => {
  const response = await fetch('https://us-central1-petid-77a78.cloudfunctions.net/generatePetBiometric', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': \`Bearer \${apiKey}\`
    },
    body: JSON.stringify({
      images: images,
      petData: petData
    })
  });
  
  return await response.json();
};

// Usage - Multiple images required for advanced biometric
const images = [
  {
    type: "frontFace",
    base64Data: "data:image/jpeg;base64,..."
  },
  {
    type: "leftProfile", 
    base64Data: "data:image/jpeg;base64,..."
  },
  {
    type: "rightProfile",
    base64Data: "data:image/jpeg;base64,..."
  },
  {
    type: "fullBody",
    base64Data: "data:image/jpeg;base64,..."
  }
];

const petData = {
  species: "Dog",
  breed: "Golden Retriever",
  age: 3
};

const result = await generatePetBiometric(images, petData, 'your-api-key');
if (result.success) {
  console.log('Biometric fingerprint:', result.biometricID.fingerprint);
  console.log('Confidence:', result.biometricID.confidence);
}`,

      "generate-pet-biometric-v2": `// Generate Pet Biometric V2 (URL-based, recommended)
const generatePetBiometricV2 = async (imageUrls, petData, apiKey) => {
  const response = await fetch('https://us-central1-petid-77a78.cloudfunctions.net/generatePetBiometricV2', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': \`Bearer \${apiKey}\`
    },
    body: JSON.stringify({
      imageUrls: imageUrls,
      petData: petData
    })
  });
  
  return await response.json();
};

// Usage - Upload images to storage first, then process URLs
const imageUrls = [
  {
    type: "frontFace",
    url: "https://firebasestorage.googleapis.com/v0/b/your-bucket/o/pets%2Ffront.jpg?alt=media"
  },
  {
    type: "leftProfile", 
    url: "https://firebasestorage.googleapis.com/v0/b/your-bucket/o/pets%2Fleft.jpg?alt=media"
  },
  {
    type: "rightProfile",
    url: "https://firebasestorage.googleapis.com/v0/b/your-bucket/o/pets%2Fright.jpg?alt=media"
  },
  {
    type: "fullBody",
    url: "https://firebasestorage.googleapis.com/v0/b/your-bucket/o/pets%2Fbody.jpg?alt=media"
  }
];

const petData = {
  species: "Dog",
  breed: "Golden Retriever",
  age: 3
};

const result = await generatePetBiometricV2(imageUrls, petData, 'your-api-key');
if (result.success) {
  console.log('Biometric fingerprint:', result.biometricID.fingerprint);
  console.log('Confidence:', result.biometricID.confidence);
  console.log('Images processed:', result.processingStats.imagesProcessed);
}`,

      "register-pet": `// Pet Registration
const registerPet = async (petData, apiKey) => {
  const response = await fetch('https://us-central1-petid-77a78.cloudfunctions.net/registerPet', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': \`Bearer \${apiKey}\`
    },
    body: JSON.stringify(petData)
  });
  
  return await response.json();
};

// Usage
const petData = {
  name: "Buddy",
  species: "Dog",
  breed: "Golden Retriever",
  age: 3,
  imageBase64: "data:image/jpeg;base64,...",
  contactInfo: {
    name: "John Doe",
    phone: "+1234567890",
    email: "john@example.com"
  },
  description: "Friendly golden retriever"
};

const result = await registerPet(petData, 'your-api-key');`,

      "health-record": `// Health Record Update
const updateHealthRecord = async (healthData, apiKey) => {
  const response = await fetch('https://us-central1-petid-77a78.cloudfunctions.net/updateHealthRecord', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': \`Bearer \${apiKey}\`
    },
    body: JSON.stringify(healthData)
  });
  
  return await response.json();
};

// Usage
const healthData = {
  petId: "pet_123456",
  visitType: "Annual Checkup",
  date: "2024-01-15",
  veterinarian: "Dr. Smith",
  clinic: "Pet Care Clinic",
  vaccinations: [
    {
      vaccine: "DHPP",
      date_given: "2024-01-15",
      next_due: "2025-01-15"
    }
  ],
  notes: "Healthy, all vitals normal"
};

const result = await updateHealthRecord(healthData, 'your-api-key');`,
    },

    python: {
      "identify-pet": `import requests
import base64

def identify_pet(image_path, api_key):
    # Read and encode image
    with open(image_path, 'rb') as image_file:
        image_base64 = base64.b64encode(image_file.read()).decode('utf-8')
    
    url = 'https://us-central1-petid-77a78.cloudfunctions.net/identifyPet'
    headers = {
        'Content-Type': 'application/json',
        'Authorization': f'Bearer {api_key}'
    }
    
    data = {
        'imageBase64': f'data:image/jpeg;base64,{image_base64}',
        'location': 'Optional location'
    }
    
    response = requests.post(url, json=data, headers=headers)
    return response.json()

# Usage
result = identify_pet('pet_photo.jpg', 'your-api-key')
if result['success']:
    print(f"Pet identified: {result['data']}")`,

      "identify-pet-from-url": `import requests

def identify_pet_from_url(image_url, api_key):
    url = 'https://us-central1-petid-77a78.cloudfunctions.net/identifyPetFromUrl'
    headers = {
        'Content-Type': 'application/json',
        'Authorization': f'Bearer {api_key}'
    }
    
    data = {
        'imageUrl': image_url,
        'location': 'Optional location'
    }
    
    response = requests.post(url, json=data, headers=headers)
    return response.json()

# Usage - Better for large images and external URLs
result = identify_pet_from_url('https://example.com/pet-image.jpg', 'your-api-key')
if result['success']:
    print(f"Pet identified: {result['data']}")
    print(f"Source URL: {result['data']['sourceUrl']}")`,

      "register-pet": `import requests

def register_pet(pet_data, api_key):
    url = 'https://us-central1-petid-77a78.cloudfunctions.net/registerPet'
    headers = {
        'Content-Type': 'application/json',
        'Authorization': f'Bearer {api_key}'
    }
    
    response = requests.post(url, json=pet_data, headers=headers)
    return response.json()

# Usage
pet_data = {
    'name': 'Buddy',
    'species': 'Dog',
    'breed': 'Golden Retriever',
    'age': 3,
    'imageBase64': 'data:image/jpeg;base64,...',
    'contactInfo': {
        'name': 'John Doe',
        'phone': '+1234567890',
        'email': 'john@example.com'
    }
}

result = register_pet(pet_data, 'your-api-key')`,

      "health-record": `import requests
from datetime import datetime

def update_health_record(health_data, api_key):
    url = 'https://us-central1-petid-77a78.cloudfunctions.net/updateHealthRecord'
    headers = {
        'Content-Type': 'application/json',
        'Authorization': f'Bearer {api_key}'
    }
    
    response = requests.post(url, json=health_data, headers=headers)
    return response.json()

# Usage
health_data = {
    'petId': 'pet_123456',
    'visitType': 'Annual Checkup',
    'date': datetime.now().strftime('%Y-%m-%d'),
    'veterinarian': 'Dr. Smith',
    'vaccinations': [
        {
            'vaccine': 'DHPP',
            'date_given': '2024-01-15',
            'next_due': '2025-01-15'
        }
    ]
}

result = update_health_record(health_data, 'your-api-key')`,
    },

    curl: {
      "identify-pet": `# Pet Identification
curl -X POST \\
  https://us-central1-petid-77a78.cloudfunctions.net/identifyPet \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer your-api-key" \\
  -d '{
    "imageBase64": "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQ...",
    "location": "San Francisco, CA"
  }'`,

      "identify-pet-from-url": `# Pet Identification from URL
curl -X POST \\
  https://us-central1-petid-77a78.cloudfunctions.net/identifyPetFromUrl \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer your-api-key" \\
  -d '{
    "imageUrl": "https://example.com/pet-image.jpg",
    "location": "San Francisco, CA"
  }'`,

      "register-pet": `# Pet Registration
curl -X POST \\
  https://us-central1-petid-77a78.cloudfunctions.net/registerPet \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer your-api-key" \\
  -d '{
    "name": "Buddy",
    "species": "Dog",
    "breed": "Golden Retriever",
    "age": 3,
    "imageBase64": "data:image/jpeg;base64,...",
    "contactInfo": {
      "name": "John Doe",
      "phone": "+1234567890",
      "email": "john@example.com"
    }
  }'`,

      "health-record": `# Health Record Update
curl -X POST \\
  https://us-central1-petid-77a78.cloudfunctions.net/updateHealthRecord \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer your-api-key" \\
  -d '{
    "petId": "pet_123456",
    "visitType": "Annual Checkup",
    "date": "2024-01-15",
    "veterinarian": "Dr. Smith",
    "vaccinations": [
      {
        "vaccine": "DHPP",
        "date_given": "2024-01-15",
        "next_due": "2025-01-15"
      }
    ]
  }'`,
    },
  };

  const renderEndpointCard = (endpoint) => {
    const isExpanded = expandedEndpoint === endpoint.id;

    return (
      <div
        key={endpoint.id}
        className={`${themeClasses.bg.card} rounded-lg border p-6 mb-4 ${themeClasses.border.primary}`}
      >
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setExpandedEndpoint(isExpanded ? null : endpoint.id)}
        >
          <div className="flex items-center space-x-3">
            <endpoint.icon className="w-6 h-6 text-cyan-400" />
            <div>
              <h3
                className={`text-lg font-semibold ${themeClasses.text.primary}`}
              >
                {endpoint.name}
              </h3>
              <p className={`text-sm ${themeClasses.text.secondary}`}>
                {endpoint.description}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${
                endpoint.method === "GET"
                  ? "bg-green-500/20 text-green-400"
                  : endpoint.method === "POST"
                  ? "bg-blue-500/20 text-blue-400"
                  : endpoint.method === "PUT"
                  ? "bg-yellow-500/20 text-yellow-400"
                  : endpoint.method === "DELETE"
                  ? "bg-red-500/20 text-red-400"
                  : "bg-purple-500/20 text-purple-400"
              }`}
            >
              {endpoint.method}
            </span>
            {endpoint.crudOperation && (
              <span
                className={`px-2 py-1 rounded text-xs font-medium ${
                  endpoint.crudOperation === "CREATE"
                    ? "bg-green-500/20 text-green-400"
                    : endpoint.crudOperation === "READ"
                    ? "bg-blue-500/20 text-blue-400"
                    : endpoint.crudOperation === "UPDATE"
                    ? "bg-yellow-500/20 text-yellow-400"
                    : endpoint.crudOperation === "DELETE"
                    ? "bg-red-500/20 text-red-400"
                    : "bg-purple-500/20 text-purple-400"
                }`}
              >
                {endpoint.crudOperation}
              </span>
            )}
            <span
              className={`font-mono text-sm ${themeClasses.text.secondary}`}
            >
              {endpoint.path}
            </span>
            {isExpanded ? (
              <ChevronDown
                className={`w-5 h-5 ${themeClasses.text.secondary}`}
              />
            ) : (
              <ChevronRight
                className={`w-5 h-5 ${themeClasses.text.secondary}`}
              />
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="mt-6 space-y-6">
            {/* Parameters */}
            <div>
              <h4
                className={`text-md font-semibold mb-3 ${themeClasses.text.primary}`}
              >
                Parameters
              </h4>
              <div className="space-y-2">
                {Object.entries(endpoint.parameters).map(([param, details]) => (
                  <div
                    key={param}
                    className={`rounded p-3 ${themeClasses.bg.secondary}`}
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-mono text-cyan-400">{param}</span>
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          details.required
                            ? "bg-red-500/20 text-red-400"
                            : `bg-gray-500/20 ${themeClasses.text.secondary}`
                        }`}
                      >
                        {details.required ? "required" : "optional"}
                      </span>
                      <span
                        className={`text-sm ${themeClasses.text.secondary}`}
                      >
                        ({details.type})
                      </span>
                    </div>
                    <p className={`text-sm ${themeClasses.text.secondary}`}>
                      {details.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Response */}
            <div>
              <h4
                className={`text-md font-semibold mb-3 ${themeClasses.text.primary}`}
              >
                Response
              </h4>
              <div className={`rounded p-4 ${themeClasses.bg.secondary}`}>
                <pre
                  className={`text-sm overflow-x-auto ${themeClasses.text.secondary}`}
                >
                  {JSON.stringify(endpoint.response, null, 2)}
                </pre>
              </div>
            </div>

            {/* Code Example */}
            <div>
              <h4
                className={`text-md font-semibold mb-3 ${themeClasses.text.primary}`}
              >
                Code Example
              </h4>
              <div className={`rounded-lg p-4 ${themeClasses.bg.tertiary}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex space-x-2">
                    {["javascript", "python", "curl"].map((lang) => (
                      <button
                        key={lang}
                        onClick={() => setSelectedLanguage(lang)}
                        className={`px-3 py-1 rounded text-sm ${
                          selectedLanguage === lang
                            ? "bg-cyan-500 text-white"
                            : `${themeClasses.bg.secondary} ${themeClasses.text.secondary} ${themeClasses.cardHover}`
                        }`}
                      >
                        {lang.charAt(0).toUpperCase() + lang.slice(1)}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        codeExamples[selectedLanguage][endpoint.id] || "",
                        endpoint.id
                      )
                    }
                    className={`flex items-center space-x-1 ${themeClasses.text.secondary} hover:${themeClasses.text.primary}`}
                  >
                    {copiedCode === endpoint.id ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                    <span className="text-sm">Copy</span>
                  </button>
                </div>
                <pre
                  className={`text-sm overflow-x-auto ${themeClasses.text.secondary}`}
                >
                  <code>
                    {codeExamples[selectedLanguage][endpoint.id] ||
                      "Example not available for this language"}
                  </code>
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="space-y-6">
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-8 ${themeClasses.border.primary}`}
            >
              <h2
                className={`text-2xl font-bold mb-4 ${themeClasses.text.primary}`}
              >
                PetID.Neural API Overview
              </h2>
              <p className={`mb-6 ${themeClasses.text.secondary}`}>
                The PetID.Neural API provides comprehensive pet identification,
                registration, and health management capabilities. Our AI-powered
                platform offers accurate pet identification, breed detection,
                and health record management.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-cyan-500/10 rounded-lg p-4 border border-cyan-500/30">
                  <Camera className="w-8 h-8 text-cyan-400 mb-3" />
                  <h3
                    className={`text-lg font-semibold mb-2 ${themeClasses.text.primary}`}
                  >
                    Pet Identification
                  </h3>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    AI-powered pet identification from photos with high accuracy
                    rates
                  </p>
                </div>

                <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
                  <Database className="w-8 h-8 text-purple-400 mb-3" />
                  <h3
                    className={`text-lg font-semibold mb-2 ${themeClasses.text.primary}`}
                  >
                    Breed Database
                  </h3>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    Comprehensive breed information and comparison tools
                  </p>
                </div>

                <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
                  <Heart className="w-8 h-8 text-green-400 mb-3" />
                  <h3
                    className={`text-lg font-semibold mb-2 ${themeClasses.text.primary}`}
                  >
                    Health Records
                  </h3>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    Digital health record management and document extraction
                  </p>
                </div>
              </div>
            </div>

            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <h3
                className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
              >
                Base URL
              </h3>
              <div className={`rounded p-3 ${themeClasses.bg.tertiary}`}>
                <code className="text-cyan-400">
                  https://us-central1-petid-77a78.cloudfunctions.net
                </code>
              </div>
            </div>

            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <h3
                className={`text-xl font-semibold mb-4 ${themeClasses.text.primary}`}
              >
                Quick Start
              </h3>
              <ol
                className={`list-decimal list-inside space-y-2 ${themeClasses.text.secondary}`}
              >
                <li>Obtain your API key from the dashboard</li>
                <li>Include the API key in your request headers</li>
                <li>Make requests to the appropriate endpoints</li>
                <li>Handle responses and errors appropriately</li>
              </ol>
            </div>
          </div>
        );

      case "authentication":
        return (
          <div className="space-y-6">
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <h2
                className={`text-2xl font-bold mb-4 ${themeClasses.text.primary}`}
              >
                Authentication
              </h2>
              <p className={`mb-6 ${themeClasses.text.secondary}`}>
                All API requests require authentication using an API key.
                Include your API key in the Authorization header.
              </p>

              <div
                className={`rounded-lg p-4 mb-6 ${themeClasses.bg.tertiary}`}
              >
                <h3
                  className={`text-lg font-semibold mb-3 ${themeClasses.text.primary}`}
                >
                  Header Format
                </h3>
                <pre className={themeClasses.text.secondary}>
                  <code>Authorization: Bearer your-api-key-here</code>
                </pre>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <h4 className="text-yellow-400 font-semibold mb-2">
                  Security Best Practices
                </h4>
                <ul
                  className={`text-sm space-y-1 ${themeClasses.text.secondary}`}
                >
                  <li>• Never expose your API key in client-side code</li>
                  <li>• Use environment variables to store API keys</li>
                  <li>• Rotate your API keys regularly</li>
                  <li>• Monitor API key usage for suspicious activity</li>
                </ul>
              </div>
            </div>
          </div>
        );

      case "pet-management":
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2
                className={`text-2xl font-bold mb-2 ${themeClasses.text.primary}`}
              >
                Pet Management CRUD
              </h2>
              <p className={themeClasses.text.secondary}>
                Essential API endpoints for pet identification and registration
              </p>
            </div>
            {petManagementEndpoints.map(renderEndpointCard)}
          </div>
        );

      case "health-records":
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2
                className={`text-2xl font-bold mb-2 ${themeClasses.text.primary}`}
              >
                Health Records CRUD
              </h2>
              <p className={themeClasses.text.secondary}>
                Manage pet health records and extract information from documents
              </p>
            </div>
            {healthRecordsEndpoints.map(renderEndpointCard)}
          </div>
        );

      case "vaccination-records":
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2
                className={`text-2xl font-bold mb-2 ${themeClasses.text.primary}`}
              >
                Vaccination Records CRUD
              </h2>
              <p className={themeClasses.text.secondary}>
                Manage pet vaccination records and store vaccination data
              </p>
            </div>
            {vaccinationRecordsEndpoints.map(renderEndpointCard)}
          </div>
        );

      case "user-management":
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2
                className={`text-2xl font-bold mb-2 ${themeClasses.text.primary}`}
              >
                User Management CRUD
              </h2>
              <p className={themeClasses.text.secondary}>
                Manage user profiles and account information
              </p>
            </div>
            {userManagementEndpoints.map(renderEndpointCard)}
          </div>
        );

      case "api-key-management":
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2
                className={`text-2xl font-bold mb-2 ${themeClasses.text.primary}`}
              >
                API Key Management CRUD
              </h2>
              <p className={themeClasses.text.secondary}>
                Manage API keys and permissions for enterprise users
              </p>
            </div>
            {apiKeyManagementEndpoints.map(renderEndpointCard)}
          </div>
        );

      case "breed-database":
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2
                className={`text-2xl font-bold mb-2 ${themeClasses.text.primary}`}
              >
                Breed Database
              </h2>
              <p className={themeClasses.text.secondary}>
                Access comprehensive breed information and comparison tools
              </p>
            </div>
            {breedEndpoints.map(renderEndpointCard)}
          </div>
        );

      case "utility-functions":
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2
                className={`text-2xl font-bold mb-2 ${themeClasses.text.primary}`}
              >
                Utility Functions
              </h2>
              <p className={themeClasses.text.secondary}>
                Additional API endpoints for system-wide statistics and
                analytics
              </p>
            </div>
            {utilityEndpoints.map(renderEndpointCard)}
          </div>
        );

      case "rate-limits":
        return (
          <div className="space-y-6">
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <h2
                className={`text-2xl font-bold mb-4 ${themeClasses.text.primary}`}
              >
                Rate Limits
              </h2>
              <p className={`mb-6 ${themeClasses.text.secondary}`}>
                API rate limits are enforced to ensure fair usage and maintain
                service quality.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
                  <h3
                    className={`text-lg font-semibold mb-3 ${themeClasses.text.primary}`}
                  >
                    Standard Limits
                  </h3>
                  <ul className={`space-y-2 ${themeClasses.text.secondary}`}>
                    <li>• Pet Identification: 100 requests/minute</li>
                    <li>• Pet Registration: 50 requests/minute</li>
                    <li>• Health Records: 100 requests/minute</li>
                    <li>• Breed Information: 200 requests/minute</li>
                  </ul>
                </div>

                <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
                  <h3
                    className={`text-lg font-semibold mb-3 ${themeClasses.text.primary}`}
                  >
                    Response Headers
                  </h3>
                  <ul
                    className={`space-y-2 text-sm ${themeClasses.text.secondary}`}
                  >
                    <li>
                      • <code>X-RateLimit-Limit</code>: Request limit
                    </li>
                    <li>
                      • <code>X-RateLimit-Remaining</code>: Remaining requests
                    </li>
                    <li>
                      • <code>X-RateLimit-Reset</code>: Reset timestamp
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <h4 className="text-red-400 font-semibold mb-2">
                  Rate Limit Exceeded
                </h4>
                <p className={`text-sm ${themeClasses.text.secondary}`}>
                  When rate limits are exceeded, the API returns a 429 status
                  code with details about when you can retry.
                </p>
              </div>
            </div>
          </div>
        );

      case "errors":
        return (
          <div className="space-y-6">
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <h2
                className={`text-2xl font-bold mb-4 ${themeClasses.text.primary}`}
              >
                Error Handling
              </h2>
              <p className={`mb-6 ${themeClasses.text.secondary}`}>
                The API uses conventional HTTP response codes to indicate
                success or failure of requests.
              </p>

              <div className="space-y-4">
                <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
                  <h3 className="text-green-400 font-semibold mb-2">
                    Success Codes
                  </h3>
                  <ul
                    className={`text-sm space-y-1 ${themeClasses.text.secondary}`}
                  >
                    <li>
                      • <code>200</code> - OK: Request successful
                    </li>
                    <li>
                      • <code>201</code> - Created: Resource created
                      successfully
                    </li>
                  </ul>
                </div>

                <div className="bg-yellow-500/10 rounded-lg p-4 border border-yellow-500/30">
                  <h3 className="text-yellow-400 font-semibold mb-2">
                    Client Error Codes
                  </h3>
                  <ul
                    className={`text-sm space-y-1 ${themeClasses.text.secondary}`}
                  >
                    <li>
                      • <code>400</code> - Bad Request: Invalid request
                      parameters
                    </li>
                    <li>
                      • <code>401</code> - Unauthorized: Invalid or missing API
                      key
                    </li>
                    <li>
                      • <code>403</code> - Forbidden: Insufficient permissions
                    </li>
                    <li>
                      • <code>404</code> - Not Found: Resource not found
                    </li>
                    <li>
                      • <code>429</code> - Too Many Requests: Rate limit
                      exceeded
                    </li>
                  </ul>
                </div>

                <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/30">
                  <h3 className="text-red-400 font-semibold mb-2">
                    Server Error Codes
                  </h3>
                  <ul
                    className={`text-sm space-y-1 ${themeClasses.text.secondary}`}
                  >
                    <li>
                      • <code>500</code> - Internal Server Error: Server error
                    </li>
                    <li>
                      • <code>503</code> - Service Unavailable: Temporary
                      service issue
                    </li>
                  </ul>
                </div>
              </div>

              <div
                className={`mt-6 rounded-lg p-4 ${themeClasses.bg.tertiary}`}
              >
                <h3
                  className={`text-lg font-semibold mb-3 ${themeClasses.text.primary}`}
                >
                  Error Response Format
                </h3>
                <pre className={`text-sm ${themeClasses.text.secondary}`}>
                  <code>
                    {JSON.stringify(
                      {
                        success: false,
                        error: {
                          code: "INVALID_PARAMETER",
                          message: "The imageBase64 parameter is required",
                          details:
                            "Please provide a valid base64 encoded image",
                        },
                      },
                      null,
                      2
                    )}
                  </code>
                </pre>
              </div>
            </div>
          </div>
        );

      case "sdks":
        return (
          <div className="space-y-6">
            <div
              className={`${themeClasses.bg.card} rounded-lg border p-6 ${themeClasses.border.primary}`}
            >
              <h2
                className={`text-2xl font-bold mb-4 ${themeClasses.text.primary}`}
              >
                SDKs & Libraries
              </h2>
              <p className={`mb-6 ${themeClasses.text.secondary}`}>
                Official and community SDKs to help you integrate with the
                PetID.Neural API.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-yellow-500/10 rounded-lg p-4 border border-yellow-500/30">
                  <h3
                    className={`text-lg font-semibold mb-3 ${themeClasses.text.primary}`}
                  >
                    JavaScript/Node.js
                  </h3>
                  <div
                    className={`rounded p-3 mb-3 ${themeClasses.bg.tertiary}`}
                  >
                    <code className={themeClasses.text.secondary}>
                      npm install petid-neural-sdk
                    </code>
                  </div>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    Official JavaScript SDK with TypeScript support
                  </p>
                </div>

                <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
                  <h3
                    className={`text-lg font-semibold mb-3 ${themeClasses.text.primary}`}
                  >
                    Python
                  </h3>
                  <div
                    className={`rounded p-3 mb-3 ${themeClasses.bg.tertiary}`}
                  >
                    <code className={themeClasses.text.secondary}>
                      pip install petid-neural
                    </code>
                  </div>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    Official Python SDK with async support
                  </p>
                </div>

                <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
                  <h3
                    className={`text-lg font-semibold mb-3 ${themeClasses.text.primary}`}
                  >
                    PHP
                  </h3>
                  <div
                    className={`rounded p-3 mb-3 ${themeClasses.bg.tertiary}`}
                  >
                    <code className={themeClasses.text.secondary}>
                      composer require petid/neural-sdk
                    </code>
                  </div>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    Community-maintained PHP SDK
                  </p>
                </div>

                <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
                  <h3
                    className={`text-lg font-semibold mb-3 ${themeClasses.text.primary}`}
                  >
                    Ruby
                  </h3>
                  <div
                    className={`rounded p-3 mb-3 ${themeClasses.bg.tertiary}`}
                  >
                    <code className={themeClasses.text.secondary}>
                      gem install petid_neural
                    </code>
                  </div>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    Community-maintained Ruby gem
                  </p>
                </div>
              </div>

              <div className="mt-6 bg-cyan-500/10 rounded-lg p-4 border border-cyan-500/30">
                <h4 className="text-cyan-400 font-semibold mb-2">
                  Need an SDK for another language?
                </h4>
                <p className={`text-sm ${themeClasses.text.secondary}`}>
                  Contact our developer support team or contribute to our
                  open-source SDK collection on GitHub.
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1
          className={`text-4xl font-bold mb-4 flex items-center justify-center space-x-3 ${themeClasses.text.primary}`}
        >
          <BookOpen className="w-10 h-10 text-cyan-400" />
          <span>PetID.Neural API Documentation</span>
        </h1>
        <p
          className={`text-xl max-w-3xl mx-auto ${themeClasses.text.secondary}`}
        >
          Comprehensive API documentation for developers integrating with the
          PetID.Neural platform
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar Navigation */}
        <div className="lg:w-1/4">
          <div
            className={`${themeClasses.bg.card} rounded-lg border p-4 sticky top-6 ${themeClasses.border.primary}`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${themeClasses.text.primary}`}
            >
              Documentation
            </h3>
            <nav className="space-y-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    activeSection === section.id
                      ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                      : `${themeClasses.text.secondary} hover:${themeClasses.text.primary} ${themeClasses.cardHover}`
                  }`}
                >
                  <section.icon className="w-4 h-4" />
                  <span className="text-sm">{section.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:w-3/4">{renderSection()}</div>
      </div>
    </div>
  );
};

export default ApiDocumentationDetailed;
